/**
 * Catálogo de tiers e formatos suportados — espelho client-side da fonte de
 * verdade do backend (`makepub_shared.entitlements`) usada para a **validação
 * antes do envio** (R19.5).
 *
 * O Control_Panel não tem acesso ao tier real do tenant pela API pública (o
 * `tenant_id`/tier é resolvido server-side a partir da API key). Para validar
 * formato e tamanho antes do upload de forma honesta, o usuário seleciona o seu
 * plano nas configurações; a validação usa o limite do plano escolhido. O
 * backend continua sendo a autoridade final (recusa por tamanho vira `FAILED`
 * com `FileTooLarge`, R1.6) — a validação local apenas evita uploads que
 * certamente falhariam e dá uma mensagem clara e acionável.
 *
 * Os valores abaixo devem casar com `TIER_ENTITLEMENTS` do backend:
 *   free    → 5 MB,  MD/HTML/TXT
 *   starter → 25 MB, + PDF/DOCX
 *   pro     → 50 MB, + PDF/DOCX
 */

/** 1 MiB em bytes (mesma base binária do backend, `MEGABYTE`). */
export const MEGABYTE = 1024 * 1024;

/** Formatos de origem aceitos pela Ingestion_API (R1.3 / R4.1). */
export const SOURCE_FORMATS = ["pdf", "docx", "md", "txt", "html"] as const;
export type SourceFormat = (typeof SOURCE_FORMATS)[number];

/** Identificadores de tier do produto (AD-6). */
export const TIERS = ["free", "starter", "pro"] as const;
export type Tier = (typeof TIERS)[number];

export interface TierInfo {
  readonly id: Tier;
  /** Rótulo amigável exibido na UI. */
  readonly label: string;
  /** Tamanho máximo de arquivo, em bytes (R1.6/R13.3). */
  readonly maxFileBytes: number;
  /** Formatos que o tier pode enviar (R13.5). */
  readonly formats: readonly SourceFormat[];
}

const BASE_FORMATS: readonly SourceFormat[] = ["md", "html", "txt"];
const PAID_FORMATS: readonly SourceFormat[] = ["md", "html", "txt", "pdf", "docx"];

export const TIER_CATALOG: Record<Tier, TierInfo> = {
  free: {
    id: "free",
    label: "Free",
    maxFileBytes: 5 * MEGABYTE,
    formats: BASE_FORMATS,
  },
  starter: {
    id: "starter",
    label: "Starter",
    maxFileBytes: 25 * MEGABYTE,
    formats: PAID_FORMATS,
  },
  pro: {
    id: "pro",
    label: "Pro",
    maxFileBytes: 50 * MEGABYTE,
    formats: PAID_FORMATS,
  },
};

/** Plano padrão assumido antes de o usuário escolher (o mais restritivo). */
export const DEFAULT_TIER: Tier = "free";

/** Retorna o `TierInfo` de um id, caindo no plano padrão se desconhecido. */
export function tierInfo(tier: Tier | string | null | undefined): TierInfo {
  if (tier && tier in TIER_CATALOG) {
    return TIER_CATALOG[tier as Tier];
  }
  return TIER_CATALOG[DEFAULT_TIER];
}

/** Extensões de arquivo mapeadas para o `source_format` da API. */
export const EXTENSION_TO_FORMAT: Record<string, SourceFormat> = {
  pdf: "pdf",
  docx: "docx",
  md: "md",
  markdown: "md",
  txt: "txt",
  text: "txt",
  html: "html",
  htm: "html",
};

/** Rótulos legíveis por formato (para mensagens ao usuário). */
export const FORMAT_LABELS: Record<SourceFormat, string> = {
  pdf: "PDF",
  docx: "Word (.docx)",
  md: "Markdown (.md)",
  txt: "Texto (.txt)",
  html: "HTML",
};
